import { Button, Field, Input } from "@fluentui/react-components";
import { useNavigate, useLocation } from "react-router-dom";
import { ROUTES } from "#root/routes/constants";
import ButtonGroup from "#root/components/ButtonGroup";
import { PERSON_ENUMS } from "#root/utils/persons";
import { useCurrentUser } from "#root/queries/local";
import { LEVEL_OF_TREATMENT_ENUM, SERVICE_PROVIDED_ENUM, TRANSPORTATION_TO_HOSPITAL_LABEL, TREAT_AND_RELEASE_LABEL } from "#root/utils/contacts";
import InfiniteDropdown from "#root/components/InfiniteDropdown";
import { useInfiniteDropdown } from "#root/hooks/useInfiniteDropdown";
import { useLocalDepartment, useLocalUnitId, useVTrackerSelectedIncident } from "#root/queries/local";
import { useInfiniteIncidents, useIncidentById, Incident } from "#root/queries/incidents";
import { useInfiniteLocations, useLocationById, Location } from "#root/queries/locations";
import { GeneralContact } from "#root/queries/generalContacts";
import "./styles.scss";
import { useState } from "react";
import { getUnitEnumLabelByUnit } from "#root/utils/enums";
import { usePreventNavigation } from "#root/services/offlineNavigationService";
import { ChiefComplaint, useChiefComplaintById, useInfiniteChiefComplaints } from "#root/queries/chiefComplaints";

interface ContactFormProps {
    initialData?: GeneralContact;
    onSubmit: (contact: Omit<ContactFormData, 'id'>) => Promise<void>;
    submitButtonText: string;
    isSubmitting: boolean;
}

export interface ContactFormData {
    unitId: string;
    department: string;
    incidentId: string;
    firstName: string;
    lastName: string;
    age?: number;
    gender: string;
    locationId: string;
    serviceProvided: string;
    levelOfTreatment?: string;
    transportingUnit: string;
    chiefComplaintId?: string;
    otherDetails: string;
}

const initialContactFormData: ContactFormData = {
    unitId: '',
    department: '',
    incidentId: '',
    firstName: '',
    lastName: '',
    age: undefined,
    gender: '',
    locationId: '',
    serviceProvided: '',
    levelOfTreatment: undefined,
    transportingUnit: '',
    chiefComplaintId: undefined,
    otherDetails: ''
};

const ContactForm: React.FC<ContactFormProps> = ({
    initialData,
    onSubmit,
    submitButtonText,
    isSubmitting
}) => {
    const navigate = useNavigate();
    const location = useLocation();
    const isVAdmin = location.pathname.includes('/vadmin/');

    usePreventNavigation(false);

    const { data: currentUser } = useCurrentUser();
    const unit = currentUser?.unit;
    const { unitId: localUnitId, setUnitId } = useLocalUnitId();
    const { department: localDepartment, setDepartment } = useLocalDepartment();
    const { selectedIncidentId: vtrackerIncidentId, setVTrackerSelectedIncidentId } = useVTrackerSelectedIncident();

    const [contactForm, setContactForm] = useState<ContactFormData>(() => ({
        ...initialContactFormData,
        ...(initialData || {}),
        unitId: initialData?.unitId || localUnitId || '',
        department: initialData?.department || localDepartment || '',
        incidentId: initialData?.incidentId || vtrackerIncidentId || ''
    }));

    const {
        selectedData: selectedIncidentData,
        handleChange: handleIncidentChange,
        setFilter: setIncidentFilter,
        queryResult: incidentsQuery
    } = useInfiniteDropdown<Incident>({
        initialId: contactForm.incidentId || undefined,
        onChange: (id) => setVTrackerSelectedIncidentId(id),
        useInfiniteQuery: useInfiniteIncidents,
        useItemById: useIncidentById
    });

    const {
        selectedData: selectedLocationData,
        handleChange: handleLocationChange,
        setFilter: setLocationFilter,
        queryResult: locationsQuery
    } = useInfiniteDropdown<Location>({
        initialId: contactForm.locationId || undefined,
        useInfiniteQuery: useInfiniteLocations,
        useItemById: useLocationById
    });

    const {
        selectedData: selectedChiefComplaintData,
        handleChange: handleChiefComplaintChange,
        setFilter: setChiefComplaintFilter,
        queryResult: chiefComplaintsQuery
    } = useInfiniteDropdown<ChiefComplaint>({
        initialId: contactForm.chiefComplaintId || undefined,
        useInfiniteQuery: useInfiniteChiefComplaints,
        useItemById: useChiefComplaintById
    });

    const handleContactFormChange = (updates: Partial<ContactFormData>) => {
        const newContact = { ...contactForm, ...updates };
        setContactForm(newContact);

        if (updates.unitId !== undefined && updates.unitId !== contactForm.unitId) {
            setUnitId(updates.unitId || null);
        }
        if (updates.department !== undefined && updates.department !== contactForm.department) {
            setDepartment(updates.department || null);
        }
    };

    const handleFormSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (
            contactForm.serviceProvided !== TREAT_AND_RELEASE_LABEL &&
            contactForm.serviceProvided !== TRANSPORTATION_TO_HOSPITAL_LABEL
        ) {
            contactForm.levelOfTreatment = undefined;
        }

        try {
            if (selectedChiefComplaintData?.name !== "Other") {
                contactForm.otherDetails = '';
            }
            await onSubmit({
                ...contactForm,
                firstName: contactForm.firstName.trim(),
                lastName: contactForm.lastName.trim()
            });
        } catch (error) {
        }
    };

    return (
        <form autoComplete="off" onSubmit={handleFormSubmit} className={`contact-form${isSubmitting ? ' submitting' : ''}`}>
            <h3>Incident information <div className="divider"></div></h3>

            <Field label="Unit ID" required>
                <Input
                    value={contactForm.unitId}
                    onChange={(e) => handleContactFormChange({ unitId: e.target.value })}
                />
            </Field>

            <Field label="Department" required>
                <Input
                    value={contactForm.department}
                    onChange={(e) => handleContactFormChange({ department: e.target.value })}
                />
            </Field>

            <Field label="Incident" required>
                <InfiniteDropdown
                    value={contactForm.incidentId}
                    onChange={(_, data) => {
                        handleIncidentChange(data.value);
                        handleContactFormChange({ incidentId: data.value });
                    }}
                    onFilterChange={setIncidentFilter}
                    queryResult={incidentsQuery}
                    itemToOption={(incident) => ({
                        value: incident.id ?? '',
                        text: incident.name ?? ''
                    })}
                    placeholder="Select incident"
                    selectedItem={selectedIncidentData}
                />
            </Field>

            <Field label="Incident Address">
                <Input
                    value={selectedIncidentData?.address || ''}
                    readOnly
                />
            </Field>

            <h3>Contact information <div className="divider"></div></h3>

            <div className="field-two-columns">
                <Field label="Person's First Name">
                    <Input
                        value={contactForm.firstName}
                        onChange={(e) => handleContactFormChange({ firstName: e.target.value })}
                    />
                </Field>
                <Field label="Person's Last Name">
                    <Input
                        value={contactForm.lastName}
                        onChange={(e) => handleContactFormChange({ lastName: e.target.value })}
                    />
                </Field>
            </div>

            <Field label="Person's Estimated Age" required>
                <ButtonGroup
                    options={PERSON_ENUMS.AGE_RANGES.map(r => ({ label: getUnitEnumLabelByUnit(r, unit!), value: r.value }))}
                    value={contactForm.age}
                    onChange={(value) => handleContactFormChange({ age: value })}
                    name="ageRange"
                    required
                />
            </Field>

            <Field label="Person's Gender" required>
                <ButtonGroup
                    options={PERSON_ENUMS.GENDER.map(gender => ({ label: gender.label, value: gender.label }))}
                    value={contactForm.gender}
                    onChange={(value) => handleContactFormChange({ gender: value })}
                    name="gender"
                    required
                />
            </Field>

            <h3>Location information <div className="divider"></div></h3>

            <Field label="Location" required>
                <InfiniteDropdown
                    value={contactForm.locationId}
                    onChange={(_, data) => {
                        handleLocationChange(data.value);
                        handleContactFormChange({ locationId: data.value });
                    }}
                    onFilterChange={setLocationFilter}
                    queryResult={locationsQuery}
                    itemToOption={(location) => ({
                        value: location.id ?? '',
                        text: location.name ?? ''
                    })}
                    placeholder="Select location"
                    selectedItem={selectedLocationData}
                />
            </Field>

            <Field label="Location Address">
                <Input
                    value={selectedLocationData?.address || ''}
                    readOnly
                />
            </Field>

            <Field label="Service Provided" required>
                <ButtonGroup
                    options={SERVICE_PROVIDED_ENUM.map(service => ({ label: service, value: service }))}
                    value={contactForm.serviceProvided}
                    onChange={(value) => handleContactFormChange({ serviceProvided: value })}
                    name="serviceProvided"
                    required
                />
            </Field>

            {(contactForm.serviceProvided === TREAT_AND_RELEASE_LABEL ||
              contactForm.serviceProvided === TRANSPORTATION_TO_HOSPITAL_LABEL) && (
                <Field label="Level of Treatment" required>
                    <ButtonGroup
                        options={LEVEL_OF_TREATMENT_ENUM.map(l => ({ label: l, value: l }))}
                        value={contactForm.levelOfTreatment}
                        onChange={(value) => handleContactFormChange({ levelOfTreatment: value })}
                        name="levelOfTreatment"
                        required
                    />
                </Field>
            )}

            <Field label="Transporting Unit">
                <Input
                    value={contactForm.transportingUnit}
                    onChange={(e) => handleContactFormChange({ transportingUnit: e.target.value })}
                />
            </Field>

            <Field label="Chief Complaint">
                <InfiniteDropdown
                    value={contactForm.chiefComplaintId}
                    onChange={(_, data) => {
                        handleChiefComplaintChange(data?.value || '');
                        handleContactFormChange({ chiefComplaintId: data?.value || undefined });
                    }}
                    onFilterChange={setChiefComplaintFilter}
                    queryResult={chiefComplaintsQuery}
                    itemToOption={(chiefComplaint) => ({
                        value: chiefComplaint.id ?? '',
                        text: chiefComplaint.name ?? ''
                    })}
                    placeholder="Select chief complaint"
                    selectedItem={selectedChiefComplaintData}
                    clearable
                />
            </Field>

            {selectedChiefComplaintData?.name === "Other" && (
                <Field label="Other">
                    <Input
                        value={contactForm.otherDetails || ''}
                        onChange={(e) => handleContactFormChange({ otherDetails: e.target.value })}
                    />
                </Field>
            )}

            <div className="form-actions">
                <Button
                    type="submit"
                    size="large"
                    appearance="primary"
                    disabled={isSubmitting}
                >
                    {isSubmitting ? 'Submitting...' : submitButtonText}
                </Button>
                <Button 
                    appearance="secondary" 
                    size="large" 
                    onClick={() => navigate(isVAdmin ? ROUTES.VADMIN.ROOT : ROUTES.VTRACKER.ROOT)}
                >
                    Cancel
                </Button>
            </div>
        </form>
    );
};

export default ContactForm;